import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { OptionRow, OptionTile } from '../ui/OptionRow.tsx';
import { haptic } from '../engine/haptics.ts';
import { asset } from '../engine/assets.ts';
import { MOTION, CREAM, INK, LINE, ORANGE, PAPER, SHADOW_CARD, SHADOW_GLOW } from '../ui/theme.ts';

/**
 * tap-select · variant two-up · variant tiles (photo tiles, `option.asset`).
 * Answer: { value, label, index }. No CTA — it auto-advances, and a step with
 * `nextBy` branches on the value just picked.
 */
export function TapSelect({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  // never arrive with an option already chosen, even when re-entering a step
  const [selected, setSelected] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const options = content.options ?? [];
  const twoUp = step.variant === 'two-up';
  const tiles = step.variant === 'tiles';

  useEffect(() => {
    ctx.setCta({ label: null });
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [step.id]);

  const pick = (i: number) => {
    const option = options[i];
    if (!option) return;
    haptic();
    setSelected(i);
    ctx.setAnswer(key, { value: option.value, label: option.label, index: i });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ctx.next(option.value), step.autoAdvanceMs ?? MOTION.autoAdvance);
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      {twoUp ? (
        <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, justifyItems: 'center' }}>
          {options.map((o, i) => (
            <OptionTile key={o.value} label={o.label} icon={o.icon} primary={i === 0} selected={selected === i} onSelect={() => pick(i)} />
          ))}
        </div>
      ) : tiles ? (
        <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {options.map((o, i) => {
            const on = selected === i;
            // an odd last tile spans the row so nothing dangles
            const span = options.length % 2 === 1 && i === options.length - 1;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => pick(i)}
                className="fk-tile"
                style={{
                  gridColumn: span ? '1 / -1' : undefined,
                  padding: 0,
                  borderRadius: 18,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: INK,
                  background: on ? CREAM : PAPER,
                  border: `2px solid ${on ? ORANGE : LINE}`,
                  boxShadow: on ? SHADOW_GLOW : SHADOW_CARD,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all .15s',
                }}
              >
                <span style={{ display: 'block', width: '100%', aspectRatio: span ? '35 / 12' : '19 / 12', background: CREAM }}>
                  {o.asset ? <img src={asset(o.asset)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" /> : null}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', fontWeight: 800, fontSize: 15 }}>
                  {o.emoji ? <span aria-hidden="true">{o.emoji}</span> : null}
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {options.map((o, i) => (
            <OptionRow key={o.value} label={o.label} emoji={o.emoji} icon={o.icon} selected={selected === i} onSelect={() => pick(i)} />
          ))}
        </div>
      )}
    </>
  );
}

/** multi-select. Answer: { values[], labels[] }. CTA carries the count badge. */
export function MultiSelect({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const options = content.options ?? [];
  const [picked, setPicked] = useState<number[]>([]);
  const min = step.min ?? 1;
  const max = step.max ?? options.length;

  useEffect(() => {
    ctx.setCta({
      label: content.cta ?? ctx.shared.ui?.next ?? 'Next',
      disabled: picked.length < min,
      badge: picked.length ? String(picked.length) : null,
      onPress: ctx.next,
    });
  }, [step.id, picked.length, content.cta]);

  // a step that allows no pick at all still records the (empty) answer
  useEffect(() => {
    if (min === 0 && !ctx.answers[key]) ctx.setAnswer(key, { values: [], labels: [], count: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  const toggle = (i: number) => {
    haptic();
    setPicked((prev) => {
      const next = prev.includes(i) ? prev.filter((p) => p !== i) : prev.length >= max ? prev : [...prev, i];
      const sorted = [...next].sort((a, b) => a - b);
      ctx.setAnswer(key, {
        values: sorted.map((n) => options[n].value),
        labels: sorted.map((n) => options[n].label),
        count: sorted.length,
      });
      return next;
    });
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />
      <div role="group" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {options.map((o, i) => (
          <OptionRow key={o.value} label={o.label} emoji={o.emoji} icon={o.icon} checkbox role="checkbox" selected={picked.includes(i)} onSelect={() => toggle(i)} />
        ))}
      </div>
    </>
  );
}
